import { Component, OnInit, AfterViewInit, OnDestroy } from '@angular/core';
import { Router } from '@angular/router';
import { forkJoin, of } from 'rxjs';
import { catchError } from 'rxjs/operators';
import Chart from 'chart.js/auto';
import {
  TrafficFinesService,
  TrafficFineDashboardSummaryDTO,
  TrafficFineByPeriodDTO,
  TrafficFineByStatusDTO,
  TrafficFineByVehicleDTO
} from '../../../services/traffic-fines.service';
import { DashComponentConfig } from '../../../shared/interfaces/dash-component.interface';

@Component({
  selector: 'app-traffic-fines-dashboard',
  templateUrl: './traffic-fines-dashboard.component.html',
  styleUrls: ['./traffic-fines-dashboard.component.css']
})
export class TrafficFinesDashboardComponent implements OnInit, AfterViewInit, OnDestroy {
  isLoadingMain = false;
  isLoadingByMonth = false;
  error: string | null = null;

  // Filtro de período do dashboard
  startDate = '';
  endDate = '';

  // Filtro próprio do gráfico mensal
  months = 12;

  // Dados dos endpoints de dashboard
  summary: TrafficFineDashboardSummaryDTO | null = null;
  byMonth: TrafficFineByPeriodDTO[] = [];
  byPaymentStatus: TrafficFineByStatusDTO[] = [];
  topVehicles: TrafficFineByVehicleDTO[] = [];

  // Gráficos Chart.js (canvas direto)
  private chartByMonth: Chart<any> | null = null;
  private chartPaymentStatus: Chart<any> | null = null;
  private chartTopVehicles: Chart<any> | null = null;

  // Configuração dos cards KPI via app-dash-component
  dashConfig: DashComponentConfig = {
    firstRowCards: [
      {
        title: 'Total de Multas',
        value: '0',
        subtitle: 'No período selecionado',
        icon: 'fas fa-file-invoice-dollar',
        color: 'primary',
        trend: { value: 0, isPositive: true }
      },
      {
        title: 'Valor Total',
        value: 'R$ 0,00',
        subtitle: 'Soma de todas as multas',
        icon: 'fas fa-dollar-sign',
        color: 'info',
        trend: { value: 0, isPositive: false }
      },
      {
        title: 'A Pagar',
        value: '0',
        subtitle: 'R$ 0,00 pendente',
        icon: 'fas fa-clock',
        color: 'warning',
        trend: { value: 0, isPositive: false }
      }
    ],
    firstRowChart: {
      type: 'pie',
      title: 'Status de Pagamento',
      data: {
        labels: [],
        datasets: [{
          label: 'Multas',
          data: [],
          backgroundColor: ['#F59E0B', '#3B82F6', '#10B981', '#8B5CF6', '#EF4444'],
          borderWidth: 0
        }]
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: {
            position: 'left',
            labels: { usePointStyle: true, pointStyle: 'circle', padding: 16, font: { size: 13 }, color: '#374151' }
          },
          tooltip: {
            callbacks: {
              label: (ctx: any) => {
                const total = (ctx.chart.data.datasets[0].data as number[]).reduce((a, b) => a + b, 0);
                const pct = total > 0 ? ((ctx.parsed / total) * 100).toFixed(1) : '0.0';
                return `${ctx.label}: ${ctx.parsed} multa(s) (${pct}%)`;
              }
            }
          }
        },
        elements: { arc: { borderWidth: 0 } }
      }
    },
    secondRowCards: [
      {
        title: 'Vencem em 7 dias',
        value: '0',
        subtitle: 'R$ 0,00',
        icon: 'fas fa-calendar-day',
        color: 'info',
        trend: { value: 0, isPositive: false }
      },
      {
        title: 'Vencem em 30 dias',
        value: '0',
        subtitle: 'R$ 0,00',
        icon: 'fas fa-calendar-alt',
        color: 'primary',
        trend: { value: 0, isPositive: false }
      },
      {
        title: 'Em Atraso',
        value: '0',
        subtitle: 'R$ 0,00 vencido',
        icon: 'fas fa-calendar-alt',
        color: 'danger',
        trend: { value: 0, isPositive: false }
      }
    ],
    secondRowChart: {
      type: 'bar',
      title: 'Top Veículos com Multas',
      data: {
        labels: [],
        datasets: [{
          label: 'Qtd. Multas',
          data: [],
          backgroundColor: '#EF4444',
          borderRadius: 6
        } as any]
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: { display: false },
          tooltip: {
            callbacks: {
              label: (ctx: any) => `${ctx.label}: ${ctx.parsed.y} multa(s)`
            }
          }
        },
        scales: {
          x: { grid: { display: false }, ticks: { color: '#6B7280', font: { size: 12 } } },
          y: { beginAtZero: true, grid: { color: 'rgba(0,0,0,0.06)' }, ticks: { color: '#6B7280' } }
        }
      }
    }
  };

  constructor(
    private trafficFinesService: TrafficFinesService,
    private router: Router
  ) {}

  ngOnInit(): void {
    this.loadAll();
  }

  ngAfterViewInit(): void {
    // O canvas dos gráficos só existe quando !isLoading. A criação ocorre após o load.
  }

  ngOnDestroy(): void {
    this.destroyCharts();
  }

  loadAll(): void {
    this.loadMainData();
    this.loadByMonth();
  }

  private loadMainData(): void {
    this.isLoadingMain = true;
    this.error = null;

    forkJoin({
      summary: this.trafficFinesService.getDashboardSummary(this.startDate || undefined, this.endDate || undefined).pipe(
        catchError(() => of(null))
      ),
      byPayment: this.trafficFinesService.getDashboardByPaymentStatus(this.startDate || undefined, this.endDate || undefined).pipe(
        catchError(() => of([]))
      ),
      topVehicles: this.trafficFinesService.getDashboardTopVehicles(10, this.startDate || undefined, this.endDate || undefined).pipe(
        catchError(() => of([]))
      )
    }).subscribe({
      next: (results) => {
        this.summary = results.summary;
        this.byPaymentStatus = (results.summary?.finesByPaymentStatus?.length ? results.summary.finesByPaymentStatus : (results.byPayment || [])) as any[];
        this.topVehicles = (results.summary?.top10VehiclesWithMostFines?.length ? results.summary.top10VehiclesWithMostFines : (results.topVehicles || [])) as any[];

        this.updateKpiCards();
        this.updatePaymentStatusChart();
        this.updateTopVehiclesChart();

        // Forçar nova referência para disparar ngOnChanges no app-dash-component
        this.dashConfig = { ...this.dashConfig };

        this.isLoadingMain = false;
      },
      error: () => {
        this.error = 'Erro ao carregar dados do dashboard';
        this.isLoadingMain = false;
      }
    });
  }

  private loadByMonth(): void {
    this.isLoadingByMonth = true;

    this.trafficFinesService.getDashboardByMonth(this.months).pipe(
      catchError(() => of([]))
    ).subscribe({
      next: (data) => {
        this.byMonth = (data || []) as any[];
        this.isLoadingByMonth = false;
        // canvas existe sempre no DOM agora; garantir criação/atualização
        setTimeout(() => this.ensureByMonthChart(), 0);
      },
      error: () => {
        this.isLoadingByMonth = false;
      }
    });
  }

  /** Só recarrega o bloco principal quando o período está completo (início e fim) ou totalmente limpo. */
  onPeriodChange(): void {
    const hasStart = !!this.startDate?.trim();
    const hasEnd = !!this.endDate?.trim();
    if (hasStart && hasEnd) {
      this.loadMainData();
      return;
    }
    if (!hasStart && !hasEnd) {
      this.loadMainData();
    }
  }

  onMonthsChange(): void {
    this.loadByMonth();
  }

  clearPeriod(): void {
    this.startDate = '';
    this.endDate = '';
    this.loadMainData();
  }

  goToList(): void {
    this.router.navigate(['/financial/traffic-fines']);
  }

  private updateKpiCards(): void {
    if (!this.summary) return;

    const fmt = (v: number) => new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(v ?? 0);
    const total = this.summary.totalFines ?? 0;

    const totalAmount = this.firstAmount(
      this.summary.totalAmountToClient,
      (this.summary as any).TotalAmountToClient,
      this.summary.totalOriginalAmount,
      (this.summary as any).TotalOriginalAmount,
      this.summary.totalAmount
    );

    const aPagarCount =
      this.summary.countAPagar ??
      this.summary.pendingCount ??
      0;

    const aPagarAmount =
      this.summary.totalPendingAmount ??
      this.summary.amountAPagar ??
      this.summary.pendingAmount ??
      0;

    const overdueCount =
      this.summary.countOverdue ??
      this.summary.overdueCount ??
      0;

    const overdueAmount =
      this.summary.amountOverdue ??
      this.summary.overdueAmount ??
      0;

    const due7Count = this.summary.countDueNext7Days ?? this.summary.upcomingDueCount ?? 0;
    const due7Amount = this.summary.amountDueNext7Days ?? 0;

    const due30Count = this.summary.countDueNext30Days ?? 0;
    const due30Amount = this.summary.amountDueNext30Days ?? 0;

    // Série para tendências MoM (último mês vs mês anterior)
    const countSeries = this.byMonth.map(m => ({ year: (m as any).year, month: (m as any).month, value: (m as any).count ?? 0 }));
    const amountSeries = this.byMonth.map(m => ({
      year: (m as any).year,
      month: (m as any).month,
      value: this.periodClientAmount(m)
    }));

    const countGrowth = this.calculateMonthOverMonthGrowth(countSeries);
    const amountGrowth = this.calculateMonthOverMonthGrowth(amountSeries);

    // Card 1: Total de Multas
    this.dashConfig.firstRowCards[0].value = String(total);
    this.dashConfig.firstRowCards[0].subtitle = 'No período selecionado';
    if (this.dashConfig.firstRowCards[0].trend) {
      this.dashConfig.firstRowCards[0].trend.value = countGrowth.value;
      this.dashConfig.firstRowCards[0].trend.isPositive = countGrowth.isPositive;
    }

    // Card 2: Valor Total
    this.dashConfig.firstRowCards[1].value = fmt(totalAmount);
    if (this.dashConfig.firstRowCards[1].trend) {
      this.dashConfig.firstRowCards[1].trend.value = amountGrowth.value;
      this.dashConfig.firstRowCards[1].trend.isPositive = amountGrowth.isPositive;
    }

    // Card 3: A Pagar (contagem + valor no subtítulo)
    this.dashConfig.firstRowCards[2].value = String(aPagarCount);
    this.dashConfig.firstRowCards[2].subtitle = `${fmt(aPagarAmount)} pendente`;
    // trend do A Pagar não agrega (normalmente acompanha o total). Mantemos 0.
    if (this.dashConfig.firstRowCards[2].trend) {
      this.dashConfig.firstRowCards[2].trend.value = 0;
      this.dashConfig.firstRowCards[2].trend.isPositive = false;
    }

    // Cards 2ª linha: 7 dias, 30 dias, atraso
    this.dashConfig.secondRowCards[0].value = String(due7Count);
    this.dashConfig.secondRowCards[0].subtitle = fmt(due7Amount);

    this.dashConfig.secondRowCards[1].value = String(due30Count);
    this.dashConfig.secondRowCards[1].subtitle = fmt(due30Amount);

    this.dashConfig.secondRowCards[2].value = String(overdueCount);
    this.dashConfig.secondRowCards[2].subtitle = `${fmt(overdueAmount)} vencido`;
  }

  private updateCharts(): void {
    // Mantido para compat: agora a atualização é chamada pelos loads específicos
    this.updatePaymentStatusChart();
    this.updateTopVehiclesChart();
    this.ensureByMonthChart();
  }

  private updatePaymentStatusChart(): void {
    const labels = this.byPaymentStatus.map(d => d.status.replace(/_/g, ' '));
    const data = this.byPaymentStatus.map(d => d.count);
    if (this.dashConfig.firstRowChart) {
      this.dashConfig.firstRowChart.data.labels = labels;
      this.dashConfig.firstRowChart.data.datasets[0].data = data;
    }
  }

  private updateTopVehiclesChart(): void {
    const labels = this.topVehicles.map(v => v.plateNumber || v.vehiclePlate || `ID ${v.vehicleId}`);
    const data = this.topVehicles.map(v => v.count);
    if (this.dashConfig.secondRowChart) {
      this.dashConfig.secondRowChart.data.labels = labels;
      this.dashConfig.secondRowChart.data.datasets[0].data = data;
    }
  }

  // --- Gráficos de canvas direto ---

  private createByMonthChart(): void {
    const canvas = document.getElementById('chartByMonth') as HTMLCanvasElement;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    if (this.chartByMonth) this.chartByMonth.destroy();

    this.chartByMonth = new Chart(ctx, {
      type: 'bar',
      data: {
        labels: this.byMonth.map(d => d.label),
        datasets: [
          {
            label: 'Qtd. Multas',
            data: this.byMonth.map(d => d.count),
            backgroundColor: '#3B82F6',
            borderRadius: 6,
            yAxisID: 'yCount'
          },
          {
            label: 'Valor (R$)',
            data: this.byMonth.map(d => this.periodClientAmount(d)),
            type: 'line' as any,
            borderColor: '#EF4444',
            backgroundColor: 'rgba(239,68,68,0.1)',
            fill: true,
            tension: 0.4,
            yAxisID: 'yAmount',
            pointRadius: 4
          }
        ]
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        interaction: { mode: 'index', intersect: false },
        plugins: {
          legend: { display: true, position: 'top' },
          tooltip: {
            callbacks: {
              label: (ctx: any) => {
                if (ctx.dataset.label === 'Valor (R$)') {
                  return `Valor: ${new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(ctx.parsed.y)}`;
                }
                return `Qtd.: ${ctx.parsed.y}`;
              }
            }
          }
        },
        scales: {
          x: { grid: { display: false }, ticks: { color: '#6B7280', font: { size: 11 } } },
          yCount: {
            position: 'left',
            beginAtZero: true,
            title: { display: true, text: 'Qtd.', color: '#3B82F6' },
            ticks: { color: '#3B82F6' },
            grid: { color: 'rgba(0,0,0,0.05)' }
          },
          yAmount: {
            position: 'right',
            beginAtZero: true,
            title: { display: true, text: 'Valor (R$)', color: '#EF4444' },
            ticks: {
              color: '#EF4444',
              callback: (v: any) => new Intl.NumberFormat('pt-BR', { notation: 'compact' }).format(v)
            },
            grid: { display: false }
          }
        }
      }
    });
  }

  private ensureByMonthChart(): void {
    // Se ainda não criou, tenta criar (canvas pode não existir fora do *ngIf)
    if (!this.chartByMonth) {
      this.createByMonthChart();
      return;
    }

    // Atualizar dados
    this.chartByMonth.data.labels = this.byMonth.map(d => d.label);
    this.chartByMonth.data.datasets[0].data = this.byMonth.map(d => d.count);
    this.chartByMonth.data.datasets[1].data = this.byMonth.map(d => this.periodClientAmount(d));
    this.chartByMonth.update();
  }

  private destroyCharts(): void {
    [this.chartByMonth, this.chartPaymentStatus, this.chartTopVehicles]
      .forEach(c => { if (c) c.destroy(); });
  }

  periodClientAmount(row: any): number {
    return this.firstAmount(
      row?.totalFinalAmountToClient,
      row?.TotalFinalAmountToClient,
      row?.totalOriginalAmount,
      row?.TotalOriginalAmount,
      row?.totalAmount,
      row?.TotalAmount
    );
  }

  vehicleClientAmount(row: any): number {
    return this.periodClientAmount(row);
  }

  private firstAmount(...values: any[]): number {
    for (const value of values) {
      if (value == null || value === '') continue;
      const amount = Number(value);
      if (Number.isFinite(amount)) return amount;
    }
    return 0;
  }

  private calculateMonthOverMonthGrowth(series: Array<{ year?: number; month?: number; value: number }>): { value: number; isPositive: boolean } {
    const cleaned = (series || [])
      .filter(x => typeof x?.value === 'number' && !Number.isNaN(x.value))
      .map(x => ({
        year: x.year ?? 0,
        month: x.month ?? 0,
        value: x.value
      }))
      .sort((a, b) => (a.year - b.year) || (a.month - b.month));

    if (cleaned.length < 2) return { value: 0, isPositive: true };

    const prev = cleaned[cleaned.length - 2].value ?? 0;
    const curr = cleaned[cleaned.length - 1].value ?? 0;

    if (prev === 0) {
      if (curr === 0) return { value: 0, isPositive: true };
      return { value: 100, isPositive: true };
    }

    const pct = ((curr - prev) / Math.abs(prev)) * 100;
    return { value: Math.round(pct), isPositive: pct >= 0 };
  }

  formatCurrency(value: number | null | undefined): string {
    return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value ?? 0);
  }
}
